import { type JSX, useCallback, useEffect } from 'react';
import { ReceiveContextCapability } from '@doxyme/extensions-bridge';
import { useAddCapability, Specs } from '@doxyme/extensions-bridge-react';
import { bridge } from '../../../features/extensions/instances';
import { getRole } from '../../../features/realtime/realtime.selectors';
import { RealtimeRole } from '../../../features/realtime/realtime.types';
import { useAppSelector } from '../../../hooks/useReduxToolkit';
import {
  useExtensionsReceiveContextPatient,
  useExtensionsReceiveContextProvider,
  useExtensionsReceiveContextBase,
  type UseExtensionsReceiveContextBaseReturnType,
} from '../../../hooks/useExtensions/useExtensionsReceiveContext';

/**
 * ExtensionsReceiveContext component exists to provide context to the extensions.
 * This components is to implement conditional rendering of hooks for provider and patient.
 * With component it is possible to always have a base context available.
 * And provider and patient hooks are only called when the role is defined.
 */
export function ExtensionsReceiveContext(): JSX.Element | null {
  const receiveContext = useAddCapability(
    bridge,
    () => new ReceiveContextCapability(),
  );

  const role = useAppSelector(getRole);

  const baseContext = useExtensionsReceiveContextBase();

  const onContextChange = useCallback(
    (context: Specs.ReceiveContext.AppContext) => {
      receiveContext.updateContext(context);
    },
    [receiveContext],
  );

  if (role === undefined) {
    return (
      <ExtensionsReceiveContextBase
        onContextChange={onContextChange}
        baseContext={baseContext}
      />
    );
  }

  return role === RealtimeRole.Provider ? (
    <ExtensionsReceiveContextProvider
      onContextChange={onContextChange}
      baseContext={baseContext}
    />
  ) : (
    <ExtensionsReceiveContextPatient
      onContextChange={onContextChange}
      baseContext={baseContext}
    />
  );
}

export type ExtensionsReceiveContextChildProps = {
  baseContext: UseExtensionsReceiveContextBaseReturnType;
  onContextChange: (context: Specs.ReceiveContext.AppContext) => void;
};

function ExtensionsReceiveContextBase({
  baseContext,
  onContextChange,
}: ExtensionsReceiveContextChildProps): null {
  useEffect(() => {
    onContextChange(baseContext);
  }, [baseContext]);
  return null;
}

function ExtensionsReceiveContextProvider({
  baseContext,
  onContextChange,
}: ExtensionsReceiveContextChildProps): null {
  const providerContext = useExtensionsReceiveContextProvider();

  useEffect(() => {
    onContextChange({ ...baseContext, ...providerContext });
  }, [providerContext, baseContext]);
  return null;
}

function ExtensionsReceiveContextPatient({
  baseContext,
  onContextChange,
}: ExtensionsReceiveContextChildProps): null {
  const patientContext = useExtensionsReceiveContextPatient();

  useEffect(() => {
    onContextChange({ ...baseContext, ...patientContext });
  }, [patientContext, baseContext]);
  return null;
}
